import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {
    SelectItem,
    SelectTrigger,
    SelectVirtualList,
    SelectComboboxPopover,
    SelectComboboxInput,
    SelectComboboxList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {AccessGroupNameEnum, AccessType, AccessTypeCategoryEnum} from '@/features/access-types/types'
import {useAccessTypesSearchableList} from '@/features/access-types/hooks/useAccessTypesSearchableList'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {eventTypeMapToLabel} from '@/features/agenda/utils'
import {ToggleGroupListType} from '@/components/commons/toggle-group-list-type/ToggleGroupListType'
import {useGetPlannedAccesses} from '@/features/planned-accesses/queries/useGetPlannedAccesses'

interface InputSelectAccessTypeProps {
    groupName?: AccessGroupNameEnum
    category?: AccessTypeCategoryEnum
    disabled?: boolean
    label?: string
    showRawAccessName?: boolean
    value?: Pick<AccessType, 'id' | 'accessType'> | null
    onChange: (value: Pick<AccessType, 'id' | 'accessType'> | null) => void
    disabledAccessTypes?: AccessType['accessType'][]
    projectId?: string
    placeholder?: string
    errorMessage?: string
    onClear?: () => void
    isOptional?: boolean
    specializationId?: string
}

export const InputSelectAccessType = forwardRef<HTMLSelectElement, InputSelectAccessTypeProps>(
    (
        {
            category,
            groupName,
            disabled,
            label,
            onChange,
            errorMessage,
            placeholder,
            value,
            onClear,
            disabledAccessTypes,
            projectId,
            showRawAccessName,
            specializationId,
            isOptional
        },
        ref
    ) => {
        const {t} = useTranslation()
        const [isQueryEnabled, setIsQueryEnabled] = useState(false)
        const [isPopoverOpen, setIsPopoverOpen] = useState(false)
        const inputUserRef = useRef(null)
        const [listType, setListType] = useState<'planned' | 'all'>(projectId ? 'planned' : 'all')

        const {
            accessTypes: allAccessTypes,
            isLoading: isLoadingAllAccessTypes,
            isError: isErrorAllAccessTypes,
            hasNextPage,
            fetchNextPage,
            onResetSearch,
            searchValue,
            onSearch
        } = useAccessTypesSearchableList({
            groupName,
            category,
            specializationId,
            responseType: 'small',
            enabled: !disabled && isQueryEnabled
        })

        const {
            data: plannedAccesses,
            isLoading: isLoadingPlannedAccesses,
            isError: isErrorPlannedAccesses
        } = useGetPlannedAccesses({
            urlParams: {
                projectId: projectId ?? ''
            },
            options: {
                enabled: !!projectId && isQueryEnabled
            }
        })

        // Handle selection change
        const handleValueChange = useCallback(
            (selectedId: string) => {
                if (listType === 'all') {
                    const foundAccessType = allAccessTypes.find(
                        accessType => accessType && accessType.id === selectedId
                    )
                    if (foundAccessType) {
                        onChange(foundAccessType)
                    }
                } else {
                    const accessType = plannedAccesses?.plannedAccesses.find(
                        plannedAccess => plannedAccess.accessType.id === selectedId
                    )
                    if (accessType) {
                        const accessTypeData = {
                            id: accessType.accessType.id,
                            accessType: accessType.accessType.accessType
                        } as AccessType

                        onChange(accessTypeData)
                    }
                }
            },
            [listType, plannedAccesses, onChange, allAccessTypes]
        )

        // Get access types
        const accessTypes = useMemo((): Pick<AccessType, 'id' | 'accessType'>[] => {
            if (listType == 'planned') {
                return (
                    plannedAccesses?.plannedAccesses
                        .filter(plannedAccess => plannedAccess.accessType.accessType.includes(searchValue ?? ''))
                        .map(plannedAccess => ({
                            id: plannedAccess.accessType.id,
                            accessType: plannedAccess.accessType.accessType
                        })) ?? []
                )
            }

            return allAccessTypes
        }, [listType, plannedAccesses, allAccessTypes, searchValue])

        // Get loading
        const isLoading = useMemo(() => {
            return isLoadingPlannedAccesses || isLoadingAllAccessTypes
        }, [isLoadingPlannedAccesses, isLoadingAllAccessTypes])

        // Get error
        const isError = useMemo(() => {
            return isErrorPlannedAccesses || isErrorAllAccessTypes
        }, [isErrorPlannedAccesses, isErrorAllAccessTypes])

        // Set list type when projectId is provided
        useEffect(() => {
            if (projectId) {
                setListType('planned')
            } else {
                setListType('all')
            }
        }, [projectId])

        // Update popover open state
        const handleSetOpen = useCallback(
            (isOpen: boolean) => {
                setIsPopoverOpen(isOpen)
                setIsQueryEnabled(isOpen)
                if (!isOpen) {
                    onResetSearch(inputUserRef)
                }
            },
            [onResetSearch]
        )

        return (
            <Flexbox direction="column" gap={1.5} fullWidth>
                <ComboboxProvider setValue={onSearch} resetValueOnHide>
                    <SelectProvider value={value?.id ?? ''} setValue={handleValueChange} setOpen={handleSetOpen}>
                        {label && (
                            <Label>
                                {label} {isOptional && <span className="description">({t('commons:optional')})</span>}
                            </Label>
                        )}
                        <SelectTrigger
                            ref={ref}
                            placeholder={placeholder}
                            fullWidth
                            value={value?.id}
                            isLoading={isPopoverOpen && isLoading}
                            displayValue={() =>
                                value?.accessType && (
                                    <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                                        {t(eventTypeMapToLabel[value.accessType])}
                                        {onClear && (
                                            <XCloseIcon
                                                role="button"
                                                onClick={e => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    onClear()
                                                }}
                                            />
                                        )}
                                    </Flexbox>
                                )
                            }
                            hasError={!!errorMessage}
                            disabled={disabled}
                        />

                        {errorMessage && (
                            <Flexbox align="center" gap={1.5}>
                                <InputStatusIcon hasError={!!errorMessage} />
                                <InputHelpText error={errorMessage} />
                            </Flexbox>
                        )}

                        <SelectComboboxPopover>
                            {projectId && (
                                <ToggleGroupListType
                                    listType={listType}
                                    setListType={setListType}
                                    radioList={{
                                        planned: {label: 'projects:planned_accesses', value: 'planned'},
                                        all: {label: 'commons:view_all', value: 'all'}
                                    }}
                                />
                            )}
                            <SelectComboboxInput placeholder={t('commons:search_access_type')} />
                            <SelectComboboxList>
                                {isLoading ? (
                                    Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
                                ) : isError ? (
                                    <InputHelpText error={t('errors:default')} />
                                ) : accessTypes.length > 0 ? (
                                    <SelectVirtualList
                                        data={accessTypes}
                                        totalCount={accessTypes.length}
                                        overscan={100}
                                        endReached={() => hasNextPage && fetchNextPage()}
                                        itemContent={(
                                            itemIndex: number,
                                            item: Pick<AccessType, 'id' | 'accessType'>
                                        ) => (
                                            <SelectItem
                                                hasItemCheckbox
                                                key={itemIndex}
                                                value={item.id}
                                                disabled={disabledAccessTypes?.includes(item.accessType)}
                                            >
                                                {showRawAccessName
                                                    ? item.accessType
                                                    : t(eventTypeMapToLabel[item.accessType])}
                                            </SelectItem>
                                        )}
                                    />
                                ) : (
                                    <InputHelpText helpText={t('commons:no_results')} />
                                )}
                            </SelectComboboxList>
                        </SelectComboboxPopover>
                    </SelectProvider>
                </ComboboxProvider>
            </Flexbox>
        )
    }
)
